import React, { useEffect, useState } from "react";
import { Cookie, X } from "lucide-react";
import { Link, useLocation } from "react-router-dom";

const CONSENT_KEY = "store_cookie_consent_v1";

function readConsent() {
  try { return localStorage.getItem(CONSENT_KEY); } catch (err) {
    // Private mode / Safari ITP can throw — treat as no consent stored.
    console.warn("Cookie consent read failed", err?.message || err);
    return null;
  }
}
function setConsent(v) {
  try { localStorage.setItem(CONSENT_KEY, v); } catch (err) {
    console.warn("Cookie consent write failed", err?.message || err);
  }
}

export default function CookieConsent() {
  const { pathname } = useLocation();
  const [visible, setVisible] = useState(false);

  // Hide on admin routes entirely — admins don't see storefront cookie banners.
  const isAdminRoute = pathname.startsWith("/admin");

  useEffect(() => {
    if (isAdminRoute) return undefined;
    // Defer to avoid SSR-like flash
    const t = setTimeout(() => {
      if (!readConsent()) setVisible(true);
    }, 1200);
    return () => clearTimeout(t);
  }, [isAdminRoute]);

  if (isAdminRoute || !visible) return null;

  const accept = () => { setConsent("accepted"); setVisible(false); };
  const reject = () => { setConsent("rejected"); setVisible(false); };

  return (
    <div className="fixed bottom-4 left-4 right-4 z-50 md:left-auto md:right-6 md:bottom-6 md:max-w-md" data-testid="cookie-banner">
      <div className="store-shell rounded-md border border-[#E6D4CC] bg-white p-5 shadow-[0_12px_40px_-12px_rgba(0,0,0,0.15)]">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-[#F1E3DC] text-[#9D5C6C]">
            <Cookie size={18} />
          </div>
          <div className="flex-1">
            <div className="font-display text-base text-[#2B2127]">A cookie, gently placed</div>
            <p className="mt-1 text-xs leading-relaxed text-[#6B5158]">
              We use cookies for essential features and to understand how our shop is used. Read our{" "}
              <Link to="/pages/privacy" className="underline decoration-[#9D5C6C] underline-offset-2 hover:text-[#2B2127]">privacy policy</Link>.
            </p>
            <div className="mt-4 flex items-center gap-2">
              <button
                onClick={accept}
                className="rounded-full bg-[#2B2127] px-5 py-2 text-xs text-white transition-colors hover:bg-[#9D5C6C]"
                data-testid="cookie-accept"
              >
                Accept all
              </button>
              <button
                onClick={reject}
                className="rounded-full border border-[#E6D4CC] px-5 py-2 text-xs text-[#2B2127] hover:bg-[#F1E3DC]"
                data-testid="cookie-reject"
              >
                Essential only
              </button>
            </div>
          </div>
          <button onClick={reject} className="ml-1 p-1 text-[#6B5158] hover:text-[#2B2127]" aria-label="Close"><X size={16} /></button>
        </div>
      </div>
    </div>
  );
}
